import { message } from "antd";
import { action, makeObservable, observable, toJS, runInAction } from "mobx";
import { WPPost, WPPostComment, WPCategory } from "src/types/wp";
import { ecosystemDb } from "src/IndexedDb";

const defaultPostInfo: WPPost = {
  author: "",
  author_name: "",
  categories: [],
  category_name: "",
  comment_status: "",
  content: {
    protected: false,
    rendered: "",
  },
  date: "",
  date_gmt: "",
  excerpt: {
    rendered: "",
    protected: false,
  },
  featured_media: 0,
  format: "",
  guid: { rendered: "" },
  id: 1,
  link: "",
  meta: { footnotes: "" },
  jetpack_featured_media_url: "",
  modified: "",
  modified_gmt: "",
  ping_status: "",
  slug: "",
  status: "",
  sticky: false,
  tags: [],
  template: "",
  title: { rendered: "" },
  type: "",
};

class EcosystemStore {
  public loadingPosts = false;
  public appError = "";
  public categoryImportList: WPCategory[] = [];
  public postInfo = defaultPostInfo;
  public postImportList: WPPost[] = [];
  public currentPage = 1;
  public totalPages = 0;
  public term = "";
  public catId = 1;

  public postCommentImportList: WPPostComment[] = [];

  constructor(_self: any) {
    makeObservable(this, {
      loadingPosts: observable,
      appError: observable,
      categoryImportList: observable,
      postInfo: observable,
      postImportList: observable,
      currentPage: observable,
      totalPages: observable,
      term: observable,
      catId: observable,

      postCommentImportList: observable,

      init: action,
      setPostInfo: action,
      findPosts: action,
      getPostById: action,
      getCategoryById: action,
      savePosts: action,
      saveCategories: action,
      setCurrentPage: action,
      setPrevPage: action,
      setNextPage: action,
      setTotalPages: action,
      setTerm: action,
      setCatId: action,
    });
    this.init();
  }

  async init() {
    this.categoryImportList = await ecosystemDb.getCategories();
    this.postImportList = await ecosystemDb.getPosts();
    this.postInfo = await ecosystemDb.getConfigValues("postInfo");
    // console.log("post info =>", toJS(this.postInfo));
    // console.log("postImportList info =>", toJS(this.postImportList));

    this.postCommentImportList = await ecosystemDb.getPostComments();
    // this.postCommentInfo = await ecosystemDb.getConfigValues("postCommentInfo");
    // console.log("postComment info =>", toJS(this.postCommentInfo));
    // console.log(
    //   "postCommentImportList info =>",
    //   toJS(this.postCommentImportList),
    // );
  }

  async setLoadingPosts(loading: boolean) {
    runInAction(() => {
      this.loadingPosts = loading;
    });
  }

  async setCurrentPage(pageNumber: number) {
    console.log("setting page to ", pageNumber);
    this.currentPage = pageNumber;
    console.log("new currentPage is ", pageNumber);
  }

  async setPrevPage() {
    if (this.currentPage === 1) {
      return;
    }

    await this.setCurrentPage(this.currentPage - 1);
  }

  async setNextPage() {
    if (this.currentPage === this.totalPages) {
      return;
    }

    this.setCurrentPage(this.currentPage + 1);
  }

  async setTotalPages(total: number) {
    this.totalPages = total;
  }

  async setTerm(newTerm: string) {
    this.term = newTerm.trim();
    console.log("new term is", this.term);
  }

  async setCatId(newCatId: number) {
    this.catId = newCatId;
  }

  async setPostInfo(postInfo: WPPost) {
    try {
      this.postInfo = postInfo;
      await ecosystemDb.setConfigValues("postInfo", toJS(postInfo));
    } catch (error) {
      message.error((error as Error).message);
    }
  }

  findPosts(subjectToFind: string) {
    const postImportList = this.postImportList;

    try {
      const filteredData = postImportList.filter((currentPost) => {
        const { id, slug } = currentPost;

        const isIdMatch = `${id || ""}`
          .toLowerCase()
          .includes(subjectToFind.trim());

        const isSlugMatch = `${slug || ""}`
          .toLowerCase()
          .includes(subjectToFind.trim());

        const isMatch = isIdMatch || isSlugMatch;

        if (isMatch) {
          return currentPost;
        }
        return false;
      });

      return filteredData;
    } catch (_) {
      message.error("Could not find post ");
    }
  }

  async getPostBySlug(slugToFind: string) {
    const postImportList = this.postImportList;

    try {
      const existIdx = postImportList.findIndex(
        ({ slug }) => slug === slugToFind,
      );

      if (existIdx >= 0) {
        return postImportList[existIdx] as WPPost;
      }
    } catch (_) {
      message.error("Could not find post ");
    }
  }

  async getPostById(postIdToFind: number) {
    const postImportList = this.postImportList;

    try {
      const existIdx = postImportList.findIndex(
        ({ id }) => id === postIdToFind,
      );

      if (existIdx >= 0) {
        return postImportList[existIdx] as WPPost;
      }
    } catch (_) {
      message.error("Could not find post ");
    }
  }

  getCategoryById(idToFind: number) {
    const categoryImportList = this.categoryImportList;

    try {
      const existIdx = categoryImportList.findIndex(
        ({ id }) => id === idToFind,
      );

      if (existIdx >= 0) {
        return categoryImportList[existIdx] as WPCategory;
      }
    } catch (_) {
      message.error("Could not find category ");
    }
  }

  async saveCategories(categories: WPCategory[]) {
    if (!categories || !categories.length) {
      return;
    }

    for (const categoryItem of categories) {
      const existIndex = this.categoryImportList.findIndex(
        (currentPost) => currentPost.id === categoryItem.id,
      );

      if (existIndex >= 0) {
        this.categoryImportList[existIndex] = categoryItem;
      } else {
        this.categoryImportList.push(categoryItem);
      }
    }
    try {
      await ecosystemDb.setCategories(toJS(this.categoryImportList));
    } catch (error) {
      message.error((error as Error).message);
    }
  }

  async savePosts(posts: WPPost[]) {
    if (!posts || !posts.length) {
      return;
    }

    for (const postItem of posts) {
      const existIndex = this.postImportList.findIndex(
        (currentPost) => currentPost.id === postItem.id,
      );

      if (existIndex >= 0) {
        this.postImportList[existIndex] = postItem;
      } else {
        this.postImportList.push(postItem);
      }
    }
    try {
      await ecosystemDb.setPosts(toJS(this.postImportList));
    } catch (error) {
      message.error((error as Error).message);
    }
  }

  async getPostCommentsByPostId(postId: number) {
    const postCommentImportList = this.postCommentImportList;

    try {
      const filteredData = postCommentImportList.filter(
        (currentPostComment) => {
          const { post } = currentPostComment;

          const isIdMatch = post === postId;

          if (isIdMatch) {
            return currentPostComment;
          }

          return false;
        },
      );

      return filteredData;
    } catch (_) {
      message.error("Could not find postCommentInfo ");
    }
  }

  async savePostComments(postComments: WPPostComment[]) {
    if (!postComments || !postComments.length) {
      return;
    }

    for (const postItem of postComments) {
      const existIndex = this.postCommentImportList.findIndex(
        (currentPost) => currentPost.id === postItem.id,
      );

      if (existIndex >= 0) {
        this.postCommentImportList[existIndex] = postItem;
      } else {
        this.postCommentImportList.push(postItem);
      }
    }
    try {
      await ecosystemDb.setPostComments(toJS(this.postCommentImportList));
    } catch (error) {
      message.error((error as Error).message);
    }
  }
}

export default EcosystemStore;
